import { BoardEditor, CheckBox, FlexHorizontal, IBoard, Spacer, createBoard } from '@vestaboard/installables'
import { Box, Button, Card, Typography, makeStyles } from '@material-ui/core'
import { CreateMessageModal, MessageTags, useCreateMessage } from '../../message-v2'
import React, { useCallback, useState } from 'react'

import { Alert } from '@material-ui/lab'
import { Checkbox } from '../../../ui/Checkbox'
import moment from 'moment'
import { useListTemplateCategories } from '../hooks/useListTemplateCategories'
import { useTagMessage } from '../../message-v2/hooks/useTagMessage'

export interface ITemplateMessage {
  message?: IMessage
  categoryId?: string
  isFeatured?: boolean
  likes?: number
  created?: number
  id?: string
}

interface IMessageTemplateForm {
  templateMessage?: ITemplateMessage
  onCreate?: (templateMessage: ITemplateMessage) => void
  onUpdate?: (templateMessage: ITemplateMessage) => void
  onDelete?: () => void
}

interface IMessage {
  id: string
  text: string | null
  characters: Array<number[]> | null
  tags: string[] | null
  cardSize?: string
}

const useStyles = makeStyles({
  cardInner: {
    padding: 24,
    backgroundColor: '#111111'
  }
})

export const MessageTemplateForm = (props: IMessageTemplateForm) => {
  const classes = useStyles()
  const [message, setMessage] = useState<IMessage | null>(props?.templateMessage?.message || null)
  const [categoryId, setCategoryId] = useState<string | null>(props?.templateMessage?.categoryId || null)
  const [isFeatured, setIsFeatured] = useState<boolean>(props?.templateMessage?.isFeatured || false)
  const [likes] = useState<number | null>(props?.templateMessage?.likes || 0)
  const [created] = useState<number>(props?.templateMessage?.created || Date.now())
  const [error, setError] = useState<string | null>(null)
  const [isAddingMessage, setIsAddingMessage] = useState(false)
  const [tagMessageMutation] = useTagMessage()
  const { data: categoriesData } = useListTemplateCategories({
    input: {}
  })
  const [isNote, setIsNote] = useState<boolean>(message?.cardSize === '15x3')
  const [tags, setTags] = useState<string[]>(message?.tags || [])
  const [boardValue, setBoardValue] = useState<IBoard>(
    (message?.characters as IBoard) || Array(6).fill(Array(22).fill(0))
  )
  const [createMessageMutation] = useCreateMessage()

  const onSave = useCallback(async () => {
    setError(null)

    if (!categoryId) {
      setError('category')
      return
    }

    const save = props.onCreate || props.onUpdate

    if (save) {
      const createdMessage = await createMessageMutation({
        variables: {
          input: {
            characters: boardValue
          }
        }
      })

      const message =
        createdMessage?.data?.createMessage?.__typename === 'MessageV2' ? createdMessage.data.createMessage : null

      if (tags?.length) {
        await tagMessageMutation({
          variables: {
            id: message?.id || '',
            tags
          }
        })
      }

      if (!message) {
        setError('message')
        return
      }
      await save({
        message,
        categoryId,
        isFeatured,
        id: props?.templateMessage?.id || undefined
      })
    }
  }, [boardValue, categoryId, createMessageMutation, isFeatured, props, tagMessageMutation, tags])

  return (
    <>
      {isAddingMessage ? (
        <CreateMessageModal
          onClose={() => {
            setIsAddingMessage(false)
          }}
          onSaved={message => {
            setMessage(message)
          }}
        />
      ) : null}
      <Card>
        <Box className={classes.cardInner}>
          <Box style={{ marginBottom: '12px', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Checkbox
              checked={!isNote}
              label='Flagship'
              handleCheck={() => {
                setIsNote(false)
                setBoardValue(createBoard())
              }}
              color='secondary'
            />
            <Box width={12} />
            <Checkbox
              checked={isNote}
              label='Note'
              handleCheck={() => {
                setIsNote(true)
                setBoardValue(
                  createBoard({
                    rows: 3,
                    columns: 15
                  })
                )
              }}
              color='secondary'
            />
          </Box>
          <BoardEditor
            boardValue={boardValue}
            setBoardValue={setBoardValue}
            boardStyle={isNote ? 'note-black' : 'black'}
            showFilledColor
          />
          <Spacer size='large' />
          <Typography variant='h5' color='secondary'>
            Category
          </Typography>
          <Spacer size='medium' />
          <Box style={{ paddingLeft: 12, display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
            {categoriesData?.listTemplateCategories?.templateCategories.map(category => (
              <Box key={category.id} style={{ marginRight: 8 }}>
                <CheckBox
                  label={category.title}
                  checked={categoryId === category.id}
                  onValueChange={() => {
                    setError(null)
                    setCategoryId(category.id)
                  }}
                />
              </Box>
            ))}
          </Box>
          {error === 'category' ? (
            <>
              <Spacer size='small' />
              <Alert severity='error'>A category is required</Alert>
            </>
          ) : null}
          <Spacer size='large' />
          <hr />
          <Spacer size='large' />
          <Box
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              paddingLeft: 12
            }}>
            <CheckBox
              checked={isFeatured}
              onValueChange={() => {
                setIsFeatured(!isFeatured)
              }}
              label='Featured'
            />
          </Box>
          <Spacer size='large' />
          <hr />
          <Spacer size='large' />
          <Card>
            <Box style={{ padding: 24, paddingTop: 0 }}>
              <MessageTags tags={tags || []} onUpdateTagsOnChange={newTags => setTags(newTags)} />
            </Box>
          </Card>
          {props?.templateMessage?.id ? (
            <>
              <Spacer size='large' />
              <Typography color='secondary' style={{ color: '#FFF' }}>
                Likes
              </Typography>
              <Typography variant='h3' color='secondary' style={{ color: '#FFF' }}>
                {likes?.toLocaleString()}
              </Typography>
              <Spacer size='large' />
              <Typography color='secondary' style={{ color: '#FFF' }}>
                Created
              </Typography>
              <Typography variant='body2' color='secondary' style={{ color: '#FFF' }}>
                {moment(created).format('YYYY-MM-DD hh:mm a')}
              </Typography>
            </>
          ) : (
            <Spacer size='large' />
          )}
          <FlexHorizontal spaceBetween>
            <div />
            <FlexHorizontal>
              {props.onDelete ? (
                <Button
                  style={{
                    marginRight: 8
                  }}
                  size='large'
                  color='secondary'
                  variant='contained'
                  onClick={() => {
                    props?.onDelete?.()
                  }}>
                  Delete
                </Button>
              ) : null}
              {props.onUpdate ? (
                <Button size='large' color='primary' variant='contained' onClick={onSave}>
                  Update
                </Button>
              ) : null}
              {props.onCreate ? (
                <Button size='large' color='primary' variant='contained' onClick={onSave}>
                  Create
                </Button>
              ) : null}
            </FlexHorizontal>
          </FlexHorizontal>
        </Box>
      </Card>
      <Spacer size='extraLarge' />
    </>
  )
}
